/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Workflow Run Events client — SSE consumer for /api/admin/workflow-runs/:id/events.
 *
 * Why not EventSource? V1 admin auth uses `Authorization: Bearer
 * ${adminAccessToken}` from localStorage. EventSource has no way to send
 * a custom Authorization header (and adminApi does NOT set a cookie).
 * We use fetch + a manual SSE parser — same approach as runner-ui's
 * personaAgentChatClient.js, just simpler because this is GET, not POST.
 *
 * Reconnection: exponential backoff 2s → 30s. Cancellation via
 * AbortController so the hook can clean up on unmount.
 *
 * The stream signals end-of-run by closing — we propagate via onEnd().
 * The page also fetches the run snapshot once on mount and skips SSE
 * entirely if the run is already in a terminal state.
 */

const SSE_PATH = (runId) =>
  `/api/admin/workflow-runs/${encodeURIComponent(runId)}/events`;

const API_BASE = (
  process.env.REACT_APP_ADMIN_API_BASE_URL
    || (typeof window !== 'undefined'
        && window.location.hostname !== 'localhost'
        && window.location.hostname !== '127.0.0.1'
        ? 'https://skilldelivery-platform.onrender.com'
        : 'http://localhost:5000')
);

const MAX_BACKOFF_MS = 30000;
const INITIAL_BACKOFF_MS = 2000;
const BACKOFF_FACTOR = 1.7;

function parseSseChunk(buffer, onEvent) {
  let idx;
  let remaining = buffer;
  while ((idx = remaining.indexOf('\n\n')) !== -1) {
    const raw = remaining.slice(0, idx);
    remaining = remaining.slice(idx + 2);

    let eventName = 'message';
    const dataLines = [];
    raw.split('\n').forEach((line) => {
      if (line.startsWith('event:')) eventName = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    });
    const dataStr = dataLines.join('\n');
    let data = {};
    if (dataStr) {
      try { data = JSON.parse(dataStr); } catch (_) { data = { raw: dataStr }; }
    }
    onEvent(eventName, data);
  }
  return remaining;
}

/**
 * Open an SSE connection to the workflow run events stream.
 *
 * @param {string} runId
 * @param {object} handlers
 *   - onOpen(): connection established
 *   - onEvent(eventName: string, data: object): every parsed SSE frame
 *   - onError(err: { message, kind, status? }): non-fatal stream error
 *   - onEnd(): upstream finished (graceful close)
 *   - onReconnecting(attempt: number, delayMs: number): about to retry
 * @returns {{ close: () => void }}
 */
export function openRunEventStream(runId, handlers = {}) {
  const controller = new AbortController();
  let closed = false;
  let attempt = 0;
  let backoff = INITIAL_BACKOFF_MS;

  const close = () => {
    closed = true;
    try { controller.abort(); } catch (_) { /* ignore */ }
  };

  const connectOnce = async () => {
    const token = (typeof window !== 'undefined' && window.localStorage)
      ? window.localStorage.getItem('adminAccessToken')
      : null;
    const headers = { Accept: 'text/event-stream' };
    if (token) headers.Authorization = `Bearer ${token}`;

    let response;
    try {
      response = await fetch(`${API_BASE}${SSE_PATH(runId)}`, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });
    } catch (err) {
      if (err.name === 'AbortError') return { retry: false };
      handlers.onError?.({ message: err.message || 'Network error', kind: 'network' });
      return { retry: true };
    }

    if (response.status === 401) {
      handlers.onError?.({ message: 'Session expired', kind: 'auth', status: 401 });
      return { retry: false };
    }

    if (response.status === 403) {
      handlers.onError?.({ message: 'Permission denied', kind: 'permission', status: 403 });
      return { retry: false };
    }

    if (response.status === 404) {
      // Run not found — terminal, no point retrying.
      handlers.onError?.({ message: 'Run not found', kind: 'not_found', status: 404 });
      return { retry: false };
    }

    if (!response.ok || !response.body) {
      let body = null;
      try { body = await response.json(); } catch (_) { /* ignore */ }
      handlers.onError?.({
        message: body?.error || `Event stream returned ${response.status}`,
        kind: 'http',
        status: response.status,
      });
      // Retry on 5xx (V2 may have hiccuped). 4xx other than the ones above:
      // surface, don't retry.
      return { retry: response.status >= 500 };
    }

    handlers.onOpen?.();
    attempt = 0;
    backoff = INITIAL_BACKOFF_MS;

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
      while (!closed) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = parseSseChunk(buffer, (name, data) => {
          handlers.onEvent?.(name, data);
        });
      }
      // Drain any trailing buffer (best effort).
      if (buffer) {
        parseSseChunk(buffer + '\n\n', (name, data) => {
          handlers.onEvent?.(name, data);
        });
      }
      handlers.onEnd?.();
      return { retry: false };
    } catch (err) {
      if (err.name === 'AbortError') return { retry: false };
      handlers.onError?.({ message: err.message, kind: 'stream' });
      // Stream broke mid-flight — try to reconnect, V2 supports
      // last-event-id replay eventually; for now we just rejoin.
      return { retry: true };
    }
  };

  (async () => {
    while (!closed) {
      const { retry } = await connectOnce();
      if (!retry || closed) return;
      attempt += 1;
      const currentBackoff = backoff;
      handlers.onReconnecting?.(attempt, currentBackoff);
      await new Promise((resolve) => {
        setTimeout(resolve, currentBackoff);
      });
      backoff = Math.min(MAX_BACKOFF_MS, Math.floor(backoff * BACKOFF_FACTOR));
    }
  })();

  return { close };
}

export default openRunEventStream;
